import React, { useState, useEffect } from 'react';
import { Star, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ rating, onRate, interactive = false, size = 'w-5 h-5' }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <button key={star} type="button" disabled={!interactive} onClick={() => interactive && onRate?.(star)} className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'} data-testid={`star-${star}`}>
        <Star className={`${size} ${star <= rating ? 'text-gold fill-gold' : 'text-muted-foreground/30'}`} />
      </button>
    ))}
  </div>
);

const ReviewSection = ({ storyId }) => {
  const { isAuthenticated, token } = useAuth();
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [total, setTotal] = useState(0);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetchReviews(); }, [storyId]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/${storyId}`);
      setReviews(res.data.reviews || []);
      setAvgRating(res.data.average_rating || 0);
      setTotal(res.data.total || 0);
    } catch (e) { /* Ignore */ }
  };

  const submitReview = async () => {
    if (myRating === 0) { toast.error(t('reviews.choose_stars')); return; }
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/reviews/${storyId}`, { rating: myRating, comment: myComment || null }, { headers });
      toast.success(t('reviews.success'));
      setShowForm(false);
      setMyRating(0);
      setMyComment('');
      fetchReviews();
    } catch (e) {
      toast.error(t('misc.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mt-6" data-testid="review-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <StarRating rating={Math.round(avgRating)} />
          <span className="text-sm text-muted-foreground">
            {avgRating > 0 ? `${avgRating} (${total})` : t('reviews.no_reviews')}
          </span>
        </div>
        {isAuthenticated && !showForm && (
          <Button data-testid="write-review-btn" variant="outline" size="sm" onClick={() => setShowForm(true)} className="rounded-full text-xs">
            {t('reviews.write')}
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-xl p-4 mb-4" data-testid="review-form">
          <p className="text-sm font-medium mb-2">{t('reviews.your_review')}</p>
          <StarRating rating={myRating} onRate={setMyRating} interactive size="w-7 h-7" />
          <Textarea data-testid="review-comment" placeholder={t('reviews.placeholder')} value={myComment} onChange={(e) => setMyComment(e.target.value)} rows={2} className="mt-3 resize-none text-sm" />
          <div className="flex gap-2 mt-3">
            <Button data-testid="submit-review-btn" onClick={submitReview} disabled={submitting || myRating === 0} size="sm" className="rounded-full bg-gold hover:bg-gold/90 text-night-blue font-bold">
              <Send className="w-3 h-3 mr-1" />
              {t('reviews.submit')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="rounded-full">
              {t('reviews.cancel')}
            </Button>
          </div>
        </div>
      )}

      {reviews.length > 0 && (
        <div className="space-y-3 max-h-48 overflow-y-auto">
          {reviews.slice(0, 5).map((review) => (
            <div key={review.id} className="bg-muted/30 rounded-lg p-3" data-testid={`review-${review.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <StarRating rating={review.rating} size="w-3.5 h-3.5" />
                <span className="text-xs font-medium text-foreground">{review.user_name}</span>
              </div>
              {review.comment && <p className="text-sm text-muted-foreground">{review.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewSection;
